import {Record, List, Set, Map} from 'immutable';

import {makeReducer, match} from 'web-app/util/redux';

import * as Actions from './actions';

export type DayType = 'available' | 'weekend' | 'closed';

export interface Week {
    title: string;
    days: List<{
        date: string;
        // eslint-disable-next-line camelcase
        date_localdate: string;
        type: DayType;
    }>;
}

interface Recipient {
    value: string;
    label: string;
    subtitle?: string;
    image?: string;
}

export class VacationPlanModalState extends Record<{
    readonly isVisible: boolean;
    readonly institutionSetId?: string;
    readonly showAskForDetailedAttendance: boolean;
    readonly fetchingRecipients: boolean;
    readonly recipientOptions: Recipient[];
    readonly updatingPossibleDays: boolean;
    readonly possibleDays: List<Week>;
    readonly excludedDays: Set<string>;
    readonly dayComments: Map<string, string | undefined>;
    readonly submitting: boolean;
}>({
    isVisible: false,
    institutionSetId: undefined,
    showAskForDetailedAttendance: false,
    fetchingRecipients: false,
    recipientOptions: [],
    updatingPossibleDays: false,
    possibleDays: List(),
    excludedDays: Set<string>(),
    dayComments: Map<string, string>(),
    submitting: false,
}) {}

export default makeReducer<VacationPlanModalState>(
    [
        match(Actions.show, (state, {payload}) => {
            return state.merge({
                isVisible: true,
                institutionSetId: payload.institutionSetId,
                showAskForDetailedAttendance: payload.showAskForDetailedAttendance,
            });
        }),
        match(Actions.hide, () => {
            return new VacationPlanModalState();
        }),
        match(Actions.periodUpdated, state => {
            return state.merge({
                updatingPossibleDays: true,
            });
        }),
        match(Actions.periodUpdatedSuccess, (state, {payload}) => {
            return state.merge({
                updatingPossibleDays: false,
                possibleDays: List(
                    payload.response.map(week => {
                        return {
                            title: week.title,
                            days: List(week.days),
                        };
                    }),
                ),
            });
        }),
        match(Actions.periodUpdatedFailed, state => {
            return state.merge({
                updatingPossibleDays: false,
            });
        }),
        match(Actions.periodUpdatedAborted, state => {
            return state.merge({
                updatingPossibleDays: false,
            });
        }),
        match(Actions.fetchRecipients, state => {
            return state.merge({
                fetchingRecipients: true,
            });
        }),
        match(Actions.fetchRecipientsSuccess, (state, {payload}) => {
            const recipientOptions = payload.response.map(recipient => ({
                value: recipient.id,
                label: recipient.name,
                subtitle: recipient.group,
                image: recipient.image,
            }));
            return state.merge({
                recipientOptions,
                fetchingRecipients: false,
            });
        }),
        match(Actions.fetchRecipientsFailed, state => {
            return state.merge({
                fetchingRecipients: false,
            });
        }),
        match(Actions.toggleExcluded, (state, {payload}) => {
            const date = payload.date;
            return state.merge({
                excludedDays: state.excludedDays.has(date)
                    ? state.excludedDays.remove(date)
                    : state.excludedDays.add(date),
            });
        }),
        match(Actions.updateComment, (state, {payload}) => {
            return state.merge({
                dayComments: state.dayComments.set(payload.date, payload.comment),
            });
        }),
        match(Actions.submit, state => {
            return state.merge({
                submitting: true,
            });
        }),
        match(Actions.submitSuccess, state => {
            return state.merge({
                submitting: false,
            });
        }),
        match(Actions.submitFailed, state => {
            return state.merge({
                submitting: false,
            });
        }),
        match(Actions.submitAborted, state => {
            return state.merge({
                submitting: false,
            });
        }),
    ],
    new VacationPlanModalState(),
);
